import {
  EqAppBar,
  EqFooter,
  EqIconButton,
  usePalette,
  useViewport,
} from "@equitable/equx";
import { useMediaQuery } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import enTranslations from "../translations/english.json";
import frTranslations from "../translations/french.json";
import { FooterTranslationValues } from "../types";

export type WrapperProps = {
  children: React.ReactNode;
  lang: "en" | "fr";
};

const Wrapper = ({ children, lang }: WrapperProps) => {
  const [translations, setTranslations] = useState<FooterTranslationValues>(
    enTranslations.footer,
  );

  const date = new Date();
  const { dividers, surface } = usePalette();
  const { isMobile } = useViewport();
  const navigate = useNavigate();
  const location = useLocation();
  const matchesHeight = useMediaQuery("(max-height: 883px");

  useEffect(() => {
    if (lang === "en") {
      document.title = "Equitable";
      setTranslations(enTranslations.footer);
    } else {
      document.title = "Équitable";
      setTranslations(frTranslations.footer);
    }
  }, [lang]);

  const handleLangChange = () => {
    switch (location.pathname) {
      case "/en/subscribe":
        navigate("/fr/abonnement");
        return;
      case "/en/success":
        navigate("/fr/reussi");
        return;
      case "/fr/abonnement":
        navigate("/en/subscribe");
        return;
      case "/fr/reussi":
        navigate("/en/success");
        return;
      case "/en/unsubscribe":
        navigate("/fr/desabonnement");
        return;
      case "/fr/desabonnement":
        navigate("/en/unsubscribe");
        return;
      default:
        return;
    }
  };

  const replaceSup = (text: string) => {
    const arr = text.split(/<sup>|<\/sup>/);

    return (
      <span>
        {arr.map((s, key) => {
          if (s === "MD" || s === "MC")
            return (
              <sup key={key} style={{ fontSize: "10px" }}>
                {s}
              </sup>
            );
          else return s;
        })}
      </span>
    );
  };

  return (
    <>
      <EqAppBar
        bgColor="surfacePrimaryDefault"
        logoText
        logoTextLang={lang === "en" ? "EN" : "FR"}
        mobileMenuClick={handleLangChange}
        rightMobileIcon={lang === "en" ? "LangFr" : "LangEn"}
        rightMobileIconStroke={0}
        upperLeftMaxLimit={5}
        upperLeftSpace={2}
        upperRight={[
          <EqIconButton
            btnIcon={lang === "en" ? "LangFr" : "LangEn"}
            stroke={0}
            onClick={handleLangChange}
            key="btn1"
          />,
        ]}
        upperRightMaxLimit={5}
        upperRightSpace={3}
        sx={{ minWidth: "100%" }}
      />

      {children}

      <div
        style={{
          borderTop: `1px solid ${dividers.default}`,
          position: isMobile || matchesHeight ? "relative" : "fixed",
          left: 0,
          bottom: 0,
          right: 0,
        }}
      >
        <EqFooter
          copyrightText={replaceSup(
            translations.copyrightText.replace(
              /{{currentYear}}/,
              date.getFullYear().toString(),
            ),
          )}
          footerLink={[
            {
              href: translations.legalUrl,
              label: translations?.legalText,
              target: "_blank",
            },
            {
              href: translations.privacyUrl,
              label: translations.privacyText,
              target: "_blank",
            },
            {
              href: translations.securityUrl,
              label: translations.securityText,
              target: "_blank",
            },
          ]}
          trademarkText={replaceSup(translations.trademarkText)}
          sx={{
            background: surface.primarySubstitute,
          }}
        />
      </div>
    </>
  );
};

export default Wrapper;
